<script>
  import { sheetJson } from '$state/data'
  import { scaleLinear, forceSimulation, forceX, forceY, forceCollide } from 'd3'
  import { onDestroy, untrack } from 'svelte'

  import BeeswarmAxes from './BeeswarmAxes.svelte'
  import BeeswarmNodes from './BeeswarmNodes.svelte'
  import BeeswarmLabels from './BeeswarmLabels.svelte'
  import DropDown from '../Search/DropDown.svelte'
  import Search from '../Search/Search.svelte'
  import Results from '../Search/Results.svelte'

  let {
    index = 0,
    offset = 0,
    query = $bindable(''),
    suggestions = $bindable([]),
    lastCardText = '',
  } = $props()

  const beeswarmData = $derived(
    sheetJson.data.filter(
      (d) =>
        (d.Probability_BTO !== undefined && d.Probability_BTO !== '') ||
        (d.Probability_Condo !== undefined && d.Probability_Condo !== ''),
    ),
  )

  let width = $state(0)
  let height = $state(0)
  const chartHeight = $derived.by(() => {
    if (width === 0 || height === 0) return 0
    const isMobile = width < 600
    return isMobile ? Math.min(480, height * 0.6) : Math.min(700, height * 0.7)
  })
  let innerWidth = $derived(width)
  let innerHeight = $derived(chartHeight > 100 ? chartHeight - 100 : 600)

  let yScale = $derived(scaleLinear().domain([0, 1]).range([innerHeight, 0]))

  const isMobile = $derived(width < 600)
  const nodeRadius = $derived(isMobile ? 2.0 : 3.0)
  const collideRadius = $derived(isMobile ? 2.9 : 4.2)

  let nodes = $state([])
  let isInitialized = false
  let simulationReady = $state(false)

  $effect(() => {
    // Read dependencies first so Svelte tracks them
    const dataLen = beeswarmData.length
    const currentWidth = width
    const currentHeight = chartHeight

    if (!dataLen || currentWidth === 0 || currentHeight === 0) return

    untrack(() => {
      simulationReady = false
      if (!isInitialized) {
        nodes = beeswarmData.map((d, i) => {
          const probBto = parseFloat(d.Probability_BTO) || 0
          const probCondo = parseFloat(d.Probability_Condo) || 0

          const descWord = String(d['descriptive-word'] || '').trim()
          let startX = innerWidth / 2 + (Math.random() - 0.5) * 10
          let startY = chartHeight / 2 + (Math.random() - 0.5) * 10

          if (typeof window !== 'undefined' && window.__wordPositions) {
            const wordPos = window.__wordPositions.get(descWord)
            if (wordPos) {
              startX = innerWidth / 2 + wordPos.x
              startY = chartHeight / 2 + wordPos.y
            }
          }

          return {
            ...d,
            id: i,
            probBto,
            probCondo,
            cx: startX,
            cy: startY,
            randomX: Math.random() * innerWidth,
            randomY: Math.random() * innerHeight,
            // D3 Physics internal coordinates
            x: startX,
            y: startY,
            targetY: yScale(probBto),
          }
        })
        isInitialized = true
      } else {
        // Re-scale target Y if height changes
        nodes.forEach((n) => {
          n.targetY = yScale(n.probBto)
        })
      }

      // Defer the heavy force simulation to a background macrotask
      // to prevent blocking Svelte's transition/mount frame.
      setTimeout(() => {
        if (!nodes.length) return

        const simulationNodes = nodes.map((n) => ({ ...n }))

        const simulation = forceSimulation(simulationNodes)
          .force('x', forceX(innerWidth / 2).strength(0.15))
          .force('y', forceY((d) => d.targetY).strength(0.4))
          .force('collide', forceCollide(collideRadius).iterations(3))
          .stop()

        // Run sim instantly to a steady state
        for (let i = 0; i < 150; i++) simulation.tick()

        simulationNodes.forEach((finalNode, i) => {
          if (nodes[i]) {
            nodes[i].finalX = finalNode.x
            nodes[i].finalY = finalNode.y
          }
        })
        if (index >= 6) {
          for (let i = 0; i < nodes.length; i++) {
            nodes[i].cx = nodes[i].finalX
            nodes[i].cy = nodes[i].finalY
          }
          const circles = nodesGroupEl ? nodesGroupEl.children : null
          if (circles) {
            for (let i = 0; i < circles.length; i++) {
              circles[i].style.transform = `translate(${nodes[i].finalX}px, ${nodes[i].finalY}px)`
            }
          }
        }
        simulationReady = true
      }, 300)
    })
  })

  // ── rAF-based lerp animation (direct DOM, bypasses Svelte reactivity) ────
  let animFrameId
  let lastIndex = -1
  let nodesGroupEl
  let hasSearched = $state(false)

  $effect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = index
    untrack(() => {
      hasSearched = false
    })
  })

  function animateToTargets(targetFn, duration = 800) {
    if (animFrameId) cancelAnimationFrame(animFrameId)

    // Snapshot current positions as start
    const starts = nodes.map((n) => ({ x: n.cx, y: n.cy }))
    const targets = nodes.map(targetFn)
    const startTime = performance.now()

    const step = (now) => {
      const elapsed = now - startTime
      const rawT = Math.min(1, elapsed / duration)
      // cubic-out easing
      const t = 1 - Math.pow(1 - rawT, 3)

      // Direct DOM writes — bypasses Svelte reactivity entirely
      const circles = nodesGroupEl ? nodesGroupEl.children : null
      if (circles) {
        for (let i = 0; i < circles.length; i++) {
          const x = starts[i].x + (targets[i].x - starts[i].x) * t
          const y = starts[i].y + (targets[i].y - starts[i].y) * t
          circles[i].style.transform = `translate(${x}px, ${y}px)`
        }
      }

      if (rawT < 1) {
        animFrameId = requestAnimationFrame(step)
      } else {
        // Sync final positions back to reactive state (one-shot)
        for (let i = 0; i < nodes.length; i++) {
          nodes[i].cx = targets[i].x
          nodes[i].cy = targets[i].y
        }
        animFrameId = null
      }
    }
    animFrameId = requestAnimationFrame(step)
  }

  let lastTargetState = 'none'

  $effect(() => {
    const currentIndex = index
    const ready = simulationReady

    untrack(() => {
      if (!ready || !nodes.length) return
      if (currentIndex === lastIndex) return
      lastIndex = currentIndex

      const targetState = currentIndex === 5 ? 'random' : currentIndex >= 6 ? 'final' : 'none'
      if (targetState === lastTargetState) return
      lastTargetState = targetState

      if (targetState === 'random') {
        animateToTargets((n) => ({ x: n.randomX, y: n.randomY }))
      } else if (targetState === 'final') {
        animateToTargets((n) => ({ x: n.finalX, y: n.finalY }))
      }
    })
  })

  $effect(() => {
    if (query && query.trim() !== '') {
      untrack(() => {
        hasSearched = true
      })
      const match = beeswarmData.some((d) => d['bto-name'] === query)
      if (match) {
        setTimeout(() => {
          const container = document.querySelector('.beeswarm-container')
          if (container) {
            const rect = container.getBoundingClientRect()
            const isOutOfView = rect.top < -50 || rect.bottom > window.innerHeight + 50
            if (isOutOfView) {
              container.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }
        }, 100)
      }
    }
  })

  onDestroy(() => {
    if (animFrameId) cancelAnimationFrame(animFrameId)
  })

  //$inspect(nodes)
</script>

<div
  class="beeswarm-container"
  bind:clientWidth={width}
  bind:clientHeight={height}
  style="opacity: {index < 6 ? 0 : 1}; pointer-events: {index < 6
    ? 'none'
    : 'auto'}; transition: opacity 0.6s ease;"
>
  {#if nodes.length > 0 && width > 0}
    <div class="dropdown-wrapper" class:visible={index >= 9}>
      {#if !hasSearched}
        <div class="top">
          <p>{lastCardText}</p>
        </div>
      {/if}
      <!--<DropDown data={beeswarmData} bind:query bind:suggestions />-->
      <Search data={beeswarmData} bind:query bind:suggestions />
      <Results {query} data={beeswarmData} />
    </div>

    <div
      class="chart-wrapper"
      class:shifted-up={index >= 9}
      style="width: 100%; height: {chartHeight}px; position: relative;"
    >
      <svg
        {width}
        height={chartHeight}
        style="width: 100%; height: 100%; overflow: visible;"
        shape-rendering="optimizeSpeed"
      >
        <defs>
          <marker id="arrow" markerWidth="10" markerHeight="10" refX="7" refY="5" orient="auto">
            <polyline
              points="3 2, 7 5, 3 8"
              fill="none"
              stroke="#fff"
              stroke-width="1"
              stroke-linejoin="miter"
              stroke-linecap="butt"
            />
          </marker>
        </defs>

        <g transform="translate(0, 50)">
          <BeeswarmAxes {yScale} {width} {index} />
          <g bind:this={nodesGroupEl}>
            <BeeswarmNodes {nodes} {query} {suggestions} {index} radius={nodeRadius} />
          </g>
        </g>
      </svg>

      <BeeswarmLabels {nodes} {width} height={chartHeight} {query} {suggestions} {index} />
    </div>
  {/if}
</div>

<style lang="scss">
  .beeswarm-container {
    width: 100%;
    height: 100lvh;
    position: relative;
    overflow: hidden;
    contain: strict;
    will-change: opacity;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .dropdown-wrapper {
    position: absolute;
    bottom: 80px;
    left: 50%;
    transform: translate(-50%, 100px);
    opacity: 0;
    width: 90%;
    max-width: 400px;
    z-index: 200;
    padding: 20px;
    box-sizing: border-box;
    transition:
      transform 0.8s cubic-bezier(0.16, 1, 0.3, 1),
      opacity 0.8s ease;

    &::before {
      content: '';
      position: absolute;
      top: -20px;
      bottom: -20px; /* Extend a bit past the bottom too */
      left: 50%;
      transform: translateX(-50%);
      width: 100vw;
      z-index: -1;
      pointer-events: none;
      background: linear-gradient(to bottom, rgba(49, 73, 156, 0) 0%, rgba(49, 73, 156, 0.8) 60px);
      backdrop-filter: blur(4px);
      -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 60px);
      mask-image: linear-gradient(to bottom, transparent 0%, black 60px);
    }

    &.visible {
      transform: translate(-50%, 0);
      opacity: 1;
    }

    .top {
      margin-bottom: 20px;
      display: flow-root;
      //padding: 0 10px;
      p {
        margin: 0;
      }
    }
  }

  .chart-wrapper {
    width: 100%;
    transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);

    &.shifted-up {
      transform: translateY(-8vh);
      @media (max-width: 600px) {
        transform: translateY(-15vh);
      }
    }
  }
</style>
